import { useState, useEffect } from 'react';

/**
 * Reads localStorage synchronously on mount so real user data is present on first paint.
 * Listens for storage events and custom events to update state when data changes.
 */
export const useLocalStorageData = (key, defaultValue = null) => {
  const [value, setValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error parsing localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    const handleUpdate = () => {
      try {
        const item = localStorage.getItem(key);
        setValue(item ? JSON.parse(item) : defaultValue);
      } catch (error) {
        console.error(`Error updating state for key "${key}":`, error);
      }
    };

    if (key === 'UserData') {
      window.addEventListener('userDataChanged', handleUpdate);
    }
    window.addEventListener('storage', handleUpdate);

    return () => {
      if (key === 'UserData') {
        window.removeEventListener('userDataChanged', handleUpdate);
      }
      window.removeEventListener('storage', handleUpdate);
    };
  }, [key, defaultValue]);

  return value;
};

const emptyUserData = {};

/**
 * Real UserData hook - available immediately on render
 */
export const useUserData = () => {
  return useLocalStorageData('UserData', emptyUserData);
};

/**
 * Real Role hook - available immediately on render
 */
export const useUserRole = () => {
  const [role, setRole] = useState(() => {
    try {
      return localStorage.getItem('Role') || '';
    } catch {
      return '';
    }
  });

  useEffect(() => {
    const handleRoleUpdate = () => {
      try {
        setRole(localStorage.getItem('Role') || '');
      } catch {
        setRole('');
      }
    };
    window.addEventListener('storage', handleRoleUpdate);
    window.addEventListener('roleChanged', handleRoleUpdate);
    return () => {
      window.removeEventListener('storage', handleRoleUpdate);
      window.removeEventListener('roleChanged', handleRoleUpdate);
    };
  }, []);

  return role;
};

/**
 * Access token hook - available immediately on render
 */
export const useAccessToken = () => {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('accessToken') || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const handleTokenUpdate = () => {
      try {
        setToken(localStorage.getItem('accessToken') || null);
      } catch {
        setToken(null);
      }
    };
    window.addEventListener('storage', handleTokenUpdate);
    return () => {
      window.removeEventListener('storage', handleTokenUpdate);
    };
  }, []);

  return token;
};

